/**
 * Rule Pattern Helpers
 *
 * Convert annotations and review markers to JSONLogic alert rules
 */

/**
 * A quality marker's JSONLogic condition plus the severity its alert rule
 * is created with. Severity used to be guessed from a substring in the
 * marker id (`getSeverityForMarker`, removed) — that guessed `high` for
 * any id containing "tone", which is how `tone_issues` became a
 * high-severity rule despite its condition being a mild sentiment dip.
 * Keeping severity explicit here means a new marker can't inherit a wrong
 * guess; it has none until someone writes one down.
 */
export interface QualityMarkerRule {
  condition: Record<string, unknown>;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

/**
 * Quality marker IDs to JSONLogic rule mapping.
 *
 * Every entry here must have a matching id in QUALITY_MARKER_KEYS
 * (analysis-utils.ts) — that list drives the checkboxes the rule is built
 * from, so a rule with no marker is unreachable, and a marker with no rule
 * makes "Create Rule from Pattern" fail for it.
 */
export const QUALITY_MARKER_RULES: Record<string, QualityMarkerRule> = {
  missing_discovery: {
    condition: { '<': [{ var: 'needsQualification.score' }, 50] },
    severity: 'high',
  },
  weak_value_proposition: {
    condition: { '<': [{ var: 'qualityScore' }, 60] },
    severity: 'medium',
  },
  tone_issues: {
    // Matches the "Negative Sentiment" preset
    // (packages/alerts/src/presets/business-rules.ts) rather than the
    // legacy `< 0` / `high` pairing that false-positived on ordinary
    // cool-but-normal calls (docs/audit/2026-07-26-alerts-review.md).
    condition: { '<': [{ var: 'sentiment.overall' }, -0.3] },
    severity: 'medium',
  },
  poor_objection_handling: {
    // Matches the "Poor Objection Handling" preset.
    condition: { '<': [{ var: 'objectionHandling.score' }, 60] },
    severity: 'medium',
  },
  incomplete_follow_up: {
    condition: { '<': [{ var: 'closingTechniques.score' }, 50] },
    severity: 'medium',
  },
};

/**
 * Annotation type to JSONLogic rule mapping
 */
export const ANNOTATION_TYPE_RULES: Record<string, Record<string, unknown>> = {
  issue: {
    '<': [{ var: 'qualityScore' }, 60],
  },
  compliance: {
    '>': [{ var: 'stopWords.detected.length' }, 0],
  },
  sentiment: {
    '<': [{ var: 'sentiment.overall' }, 0],
  },
  quality: {
    '<': [{ var: 'qualityScore' }, 70],
  },
};

/**
 * Annotation severity to rule threshold mapping
 */
export const SEVERITY_THRESHOLDS: Record<string, number> = {
  low: 70,
  medium: 60,
  high: 50,
  critical: 40,
};

/**
 * Create JSONLogic rule from quality marker
 */
export function createRuleFromMarker(markerId: string): Record<string, unknown> | null {
  return QUALITY_MARKER_RULES[markerId]?.condition ?? null;
}

/**
 * Create JSONLogic rule from annotation
 */
export function createRuleFromAnnotation(
  type: string,
  severity: string
): Record<string, unknown> | null {
  const baseRule = ANNOTATION_TYPE_RULES[type];
  if (!baseRule) return null;

  const threshold = SEVERITY_THRESHOLDS[severity] || 60;

  // Adjust threshold based on severity
  if (baseRule['<']) {
    const lessThanRule = baseRule['<'] as unknown[];
    if (Array.isArray(lessThanRule) && lessThanRule.length > 0) {
      return {
        '<': [lessThanRule[0], threshold],
      };
    }
  }

  if (baseRule['>']) {
    const greaterThanRule = baseRule['>'] as unknown[];
    if (Array.isArray(greaterThanRule) && greaterThanRule.length > 0) {
      return {
        '>': [greaterThanRule[0], threshold],
      };
    }
  }

  return baseRule;
}

/**
 * Create combined JSONLogic rule from multiple markers
 */
export function createRuleFromMarkers(
  markerIds: string[]
): Record<string, unknown> | null {
  if (markerIds.length === 0) return null;

  const rules = markerIds.map((id) => createRuleFromMarker(id)).filter((r) => r !== null);

  if (rules.length === 0) return null;

  if (rules.length === 1) return rules[0] as Record<string, unknown>;

  // Combine multiple rules with OR (any marker triggers alert)
  return {
    or: rules,
  };
}

/**
 * Get default category for marker
 */
export function getCategoryForMarker(
  markerId: string
): 'quality' | 'compliance' | 'sales' | 'sentiment' | 'custom' {
  if (
    markerId.includes('compliance') ||
    markerId.includes('disclaimer') ||
    markerId.includes('forbidden')
  ) {
    return 'compliance';
  }
  if (markerId.includes('tone') || markerId.includes('sentiment')) {
    return 'sentiment';
  }
  if (markerId.includes('discovery') || markerId.includes('value')) {
    return 'sales';
  }
  return 'quality';
}

/**
 * Render a JSONLogic condition — as produced by QUALITY_MARKER_RULES above
 * — as a short, human-readable string for confirmation UI. Only
 * understands the operators this module emits (`<`, `>`, `==`, `or`); it
 * is a preview formatter, not a general JSONLogic renderer.
 */
export function describeCondition(condition: Record<string, unknown>): string {
  const [op, args] = Object.entries(condition)[0] ?? [];
  if (!op || !Array.isArray(args)) return JSON.stringify(condition);

  if (op === 'or') {
    return (args as Record<string, unknown>[]).map(describeCondition).join(' OR ');
  }

  const [left, right] = args;
  const field =
    left && typeof left === 'object' && 'var' in left
      ? String((left as { var: unknown }).var)
      : JSON.stringify(left);
  return `${field} ${op} ${String(right)}`;
}
